import { useState, useEffect } from "react";
import { AppointmentsRepository, AppointmentJoin } from "@/repositories/appointments.repository";
import { AppointmentPhotoRepository } from "@/repositories/appointment-photos.repository";
import { AppointmentChecklistRepository } from "@/repositories/appointment-checklist.repository";
import { appointment_status, AppointmentClockData } from "@/models/appointment.model";
import { toast } from "sonner";
import { extractPhotoMetadata } from "@/lib/photo-metadata";
import { useAppointmentChecklist } from "@/hooks/useAppointmentChecklist";
import { clockPhoto, isIncompleteChecklist, isWrongStatus } from "@/lib/appointment-field-actions";
import { toAppointmentJoin } from "@/repositories/appointment-records";
import { appointmentFieldActions, reportFieldActionRefusal, reportPhotoRefusal, reportTickRefusal, reportUploadFailure } from "@/services/appointment-field-actions.service";
import { getMissingWorkflows, WORKFLOWS_NOT_LOADED_MESSAGE } from "@/lib/utils/workflow-photo-gate";

async function getGeolocation(): Promise<GeolocationPosition | null> {
  if (!navigator.geolocation) return null;

  try {
    return await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        timeout: 5000,
        maximumAge: 60000,
      });
    });
  } catch (error) {
    console.warn("Geolocation failed:", error);
    return null;
  }
}

export function useAppointmentUpdate(appointmentId: string) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appointment, setAppointment] = useState<AppointmentJoin | null>(null);

  const {
    serviceTasks,
    serviceWorkflows,
    appointmentTasks,
    showTickAnswer,
    appointmentWorkflows,
    setAppointmentWorkflows,
    checklistLoaded,
    workflowsLoaded,
    checklistError,
    retryChecklist,
    checklistProgress,
    checklistGateError,
  } = useAppointmentChecklist(appointmentId, appointment?.service?.id, !loading && !!appointment);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);

        const appt = await AppointmentsRepository.getById(appointmentId);
        if (!cancelled) setAppointment(appt);
      } catch (e: any) {
        if (!cancelled) {
          console.error("Failed to load appointment:", e);
          toast.error("Failed to load appointment details");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appointmentId]);

  const updateTaskStatus = async (taskId: string, done: boolean) => {
    try {
      showTickAnswer(await AppointmentChecklistRepository.tick(appointmentId, taskId, done));
      toast.success(done ? "Task marked as completed" : "Task marked as incomplete");
    } catch (err) {
      await reportTickRefusal(err, reReadAfterRefusedTick);
    }
  };

  const uploadPhoto = async (workflowId: string, file: File, isAfter: boolean, options?: { isCapture?: boolean }) => {
    try {
      const metadata = await extractPhotoMetadata(file, { isCapture: options?.isCapture });
      setAppointmentWorkflows(await AppointmentPhotoRepository.recordSlotPhoto(appointmentId, { workflow: workflowId, isAfter, file, metadata }));
      toast.success(`${isAfter ? "After" : "Before"} photo uploaded`);
    } catch (error) {
      await reportPhotoRefusal(error, reReadAfterRefusedPhoto);
      throw error;
    }
  };

  const reReadAppointment = async () => setAppointment(await AppointmentsRepository.getById(appointmentId));

  const reReadAfterRefusedPhoto = async () => {
    const [, photos] = await Promise.all([reReadAppointment(), AppointmentPhotoRepository.read(appointmentId)]);
    setAppointmentWorkflows(photos);
  };

  const reReadAfterRefusedTick = async () => {
    retryChecklist();
    await reReadAppointment();
  };

  const completeClockIn = async (photoUrl: string) => {
    try {
      if (appointment?.status !== appointment_status.accepted && appointment?.status !== appointment_status.goback) {
        toast.error("You can only start work when the appointment is in accepted or go back status");
        return;
      }

      const position = await getGeolocation();
      const started = await appointmentFieldActions.start(appointmentId, clockPhoto(photoUrl, position));
      setAppointment(toAppointmentJoin(started));

      toast.success("Work started. Review your before photos, then tap Begin Tasks.");
    } catch (err) {
      await reportFieldActionRefusal(err, "Failed to start work", reReadAppointment);
    }
  };

  const startWork = async () => {
    try {
      if (appointment?.status !== appointment_status.inprogress) {
        toast.error("Start work before beginning tasks");
        return;
      }

      if (appointment.meta_data?.work_started_at) return;

      if (!workflowsLoaded) {
        toast.error(WORKFLOWS_NOT_LOADED_MESSAGE);
        return;
      }

      if (getMissingWorkflows(serviceWorkflows, appointmentWorkflows, false).length > 0) {
        toast.error("Upload all before photos before beginning tasks.");
        return;
      }

      setAppointment(toAppointmentJoin(await appointmentFieldActions.beginTasks(appointmentId)));

      toast.success("Tasks begun — before photos are locked and the checklist is unlocked");
    } catch (err) {
      await reportFieldActionRefusal(err, "Failed to begin tasks", reReadAppointment);
    }
  };

  const addExtraPhoto = async (file: File, isAfter: boolean) => {
    if (appointment?.status !== appointment_status.inprogress) {
      toast.error("Additional photos can only be added while work is in progress");
      return;
    }

    const photoUrl = await AppointmentPhotoRepository.upload(appointmentId, isAfter ? "extraAfter" : "extraBefore", file).catch(reportUploadFailure);
    if (!photoUrl) return;

    try {
      setAppointment(toAppointmentJoin(await appointmentFieldActions.addExtraPhoto(appointmentId, photoUrl, isAfter)));
      toast.success(`Additional ${isAfter ? "after" : "before"} photo added`);
    } catch (err) {
      await reportFieldActionRefusal(err, "Failed to add photo", reReadAppointment);
    }
  };

  const removeExtraPhoto = async (url: string) => {
    if (appointment?.status !== appointment_status.inprogress) {
      toast.error("Additional photos can only be removed while work is in progress");
      return;
    }

    try {
      setAppointment(toAppointmentJoin(await appointmentFieldActions.removeExtraPhoto(appointmentId, url)));
      toast.success("Photo removed");
    } catch (err) {
      await reportFieldActionRefusal(err, "Failed to remove photo", reReadAppointment);
    }
  };

  const completeClockOut = async (photoUrl: string) => {
    if (appointment?.status !== appointment_status.inprogress) {
      const message = "Work can only be finished when the appointment is in progress";
      toast.error(message);
      throw new Error(message);
    }

    if (!workflowsLoaded) {
      toast.error(WORKFLOWS_NOT_LOADED_MESSAGE);
      throw new Error(WORKFLOWS_NOT_LOADED_MESSAGE);
    }

    const missingBefore = getMissingWorkflows(serviceWorkflows, appointmentWorkflows, false);
    if (missingBefore.length > 0) {
      const message = "Upload all before photos before completing work.";
      toast.error(message);
      throw new Error(message);
    }

    if (!appointment.meta_data?.work_started_at) {
      const message = "Tap Begin Tasks to lock before photos before completing work.";
      toast.error(message);
      throw new Error(message);
    }

    if (checklistGateError) {
      toast.error(checklistGateError);
      throw new Error(checklistGateError);
    }

    const missingAfter = getMissingWorkflows(serviceWorkflows, appointmentWorkflows, true);
    if (missingAfter.length > 0) {
      const message = "Upload all after photos before completing work.";
      toast.error(message);
      throw new Error(message);
    }

    try {
      const position = await getGeolocation();
      const finished = await appointmentFieldActions.finish(appointmentId, clockPhoto(photoUrl, position));
      setAppointment(toAppointmentJoin(finished));

      const clockInData = appointment?.meta_data?.clock_in as AppointmentClockData | undefined;
      if (clockInData?.time) {
        const clockInTime = new Date(clockInData.time).getTime();
        const durationMs = Date.now() - clockInTime;
        const hours = Math.floor(durationMs / (1000 * 60 * 60));
        const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
        toast.success(`Work completed. Duration: ${hours}h ${minutes}m`);
      } else {
        toast.success("Work completed successfully");
      }
    } catch (err) {
      await reportFieldActionRefusal(err, "Failed to complete work", reReadAppointment);
      if (isIncompleteChecklist(err)) retryChecklist();
      if (!isWrongStatus(err)) throw err;
    }
  };

  const saveProgress = async () => {
    setSaving(true);
    try {
      toast.success("Progress saved successfully");
    } catch (error) {
      console.error("Failed to save progress:", error);
      toast.error("Failed to save progress");
    } finally {
      setSaving(false);
    }
  };

  return {
    loading,
    saving,
    appointment,
    setAppointment,
    serviceTasks,
    serviceWorkflows,
    appointmentTasks,
    appointmentWorkflows,
    checklistLoaded,
    workflowsLoaded,
    checklistError,
    retryChecklist,
    checklistProgress,
    checklistGateError,
    updateTaskStatus,
    uploadPhoto,
    completeClockIn,
    startWork,
    addExtraPhoto,
    removeExtraPhoto,
    completeClockOut,
    saveProgress,
  };
}
